import { useMemo, useState } from "react";
import { InputAdornment } from "@mui/material";
import { FaRegCalendarAlt } from "react-icons/fa";
import DSTextField from "../../system-design/DSTextField";
import DSPopover from "../../system-design/DSPopover";
import DSBox from "../../system-design/DSBox";
import DSIconButton from "../../system-design/DSIconButton";
import Calendar, { CalendarDay } from "./Calendar";
import ScheduledKlydoDayContent from "./ScheduledKlydoDayContent";
import useCalendarEvents from "../hooks/useCalendarEvents";
import { PopulatedCalendarEvent } from "../../../api/calendarEvent";
import { Klydo } from "../../../Types";

type ScheduledDatePickerProps = {
  value: Date;
  onChange: (date: Date) => void;
  minDate?: Date;
  country?: string;
  label?: string;
};

const startOfDay = (date: Date): Date =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate());

const ScheduledDatePicker = ({
  value,
  onChange,
  minDate,
  country,
  label = "Date",
}: ScheduledDatePickerProps) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [viewMonth, setViewMonth] = useState<Date>(value);

  const open = Boolean(anchorEl);

  const handleOpen = (clickEvent: React.MouseEvent<HTMLElement>) => {
    setViewMonth(value);
    setAnchorEl(clickEvent.currentTarget);
  };
  const handleClose = () => setAnchorEl(null);

  const { data } = useCalendarEvents({
    country,
    year: viewMonth.getFullYear(),
    month: viewMonth.getMonth() + 1,
    populated: true,
    enabled: open,
  });

  const klydosByDate = useMemo(() => {
    const events = (data as PopulatedCalendarEvent[]) || [];
    const map: { [key: string]: Klydo[] } = {};
    for (const calendarEvent of events) {
      const eventDate = new Date(
        calendarEvent.date.year,
        calendarEvent.date.month - 1,
        calendarEvent.date.day,
      );
      const key = eventDate.toDateString();
      const klydos = calendarEvent.klydos || [];
      map[key] = map[key] ? [...map[key], ...klydos] : [...klydos];
    }
    return map;
  }, [data]);

  const minDay = minDate ? startOfDay(minDate) : null;

  const isDayClickable = (day: CalendarDay): boolean =>
    day.isCurrentMonth && (!minDay || startOfDay(day.date) >= minDay);

  const handleDayClick = (day: CalendarDay) => {
    if (!isDayClickable(day)) return;
    onChange(day.date);
    handleClose();
  };

  return (
    <>
      <DSTextField
        label={label}
        value={value.toLocaleDateString("en-GB")}
        onClick={handleOpen}
        fullWidth
        size="small"
        InputLabelProps={{ shrink: true }}
        InputProps={{
          readOnly: true,
          endAdornment: (
            <InputAdornment position="end">
              <DSIconButton edge="end" onClick={handleOpen}>
                <FaRegCalendarAlt />
              </DSIconButton>
            </InputAdornment>
          ),
        }}
        sx={{ "& .MuiInputBase-root": { cursor: "pointer" } }}
      />

      <DSPopover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <DSBox sx={{ width: 620, maxWidth: "95vw" }}>
          <Calendar
            currentDate={viewMonth}
            onDateChange={setViewMonth}
            renderDayContent={(day) => (
              <DSBox sx={{ height: 65, overflow: "hidden" }}>
                <ScheduledKlydoDayContent
                  day={day}
                  klydosByDate={klydosByDate}
                />
              </DSBox>
            )}
            onDayClick={handleDayClick}
            isDayClickable={isDayClickable}
            minHeight={100}
          />
        </DSBox>
      </DSPopover>
    </>
  );
};

export default ScheduledDatePicker;
